/**
 * Sets up a dialog element to function as a tooltip that appears when hovering over a specified element.
 *
 * This function:
 * - Handles browser compatibility for the HTML Dialog element
 * - If the browser supports HTMLDialogElement, it uses the native show/close methods
 * - If not supported, it falls back to using the hidden attribute
 * - Sets the dialog parent's position to 'relative'
 * - Adds a 'tooltip' class to the dialog element
 *
 * @param dialog - The HTML dialog element to be displayed as a tooltip
 * @param dialogOpener - The element that triggers the dialog when hovered over
 *
 * @example
 * ```typescript
 * const dialog = document.querySelector<HTMLDialogElement>('#my-tooltip');
 * const opener = document.querySelector<HTMLElement>('#tooltip-opener');
 *
 * if (dialog && opener) {
 *   dialogHandle(dialog, opener);
 * }
 * ```
 */
export const dialogHandle = (dialog: HTMLDialogElement, dialogOpener: HTMLElement) => {
  dialog.parentElement.style.position = 'relative'
  dialog.classList.add('tooltip')

  // check if dialog tag is supported
  if (typeof HTMLDialogElement === 'function') {
    dialog.removeAttribute('hidden')
    dialogOpener.addEventListener('mouseenter', () => dialog.show())
    dialogOpener.addEventListener('mouseout', () => dialog.close())
  } else {
    dialog.setAttribute('hidden', '')
    dialogOpener.addEventListener('mouseenter', () => dialog.removeAttribute('hidden'))
    dialogOpener.addEventListener('mouseout', () => dialog.setAttribute('hidden', ''))
  }
}

/**
 * Sets up modal dialog functionality with a fallback for browsers that don't support
 * the native HTML dialog element.
 *
 * This function:
 * - Enables opening the modal by clicking the specified opener element
 * - Closes the modal when clicking outside of it
 * - Sets up any element with the `data-modal-close` attribute as a close button
 * - Provides a fallback using hidden attribute for browsers without dialog support
 *
 * @param modal - The dialog element to be configured as a modal
 * @param modalOpener - Optional element that will trigger the modal to open when clicked
 *
 * @example
 * ```typescript
 * const modal = document.querySelector<HTMLDialogElement>('#my-modal');
 * const opener = document.querySelector<HTMLElement>('#modal-opener');
 *
 * if (modal) {
 *   modalHandle(modal, opener);
 * }
 * ```
 */
export const modalHandle = (modal: HTMLDialogElement, modalOpeners?: HTMLElement | HTMLElement[]) => {
  const closeBtn = modal.querySelector<HTMLButtonElement>('[data-modal-close]')

  // Handle multiple openers or single opener
  const attachOpenEvent = (opener: HTMLElement, action: () => void) => {
    opener.addEventListener('click', action)
  }

  const setupOpeners = (action: () => void) => {
    if (!modalOpeners) return

    if (Array.isArray(modalOpeners)) {
      modalOpeners.forEach((opener) => attachOpenEvent(opener, action))
    } else {
      attachOpenEvent(modalOpeners, action)
    }
  }

  // check if dialog tag is supported
  if (typeof HTMLDialogElement === 'function') {
    modal.removeAttribute('hidden')
    setupOpeners(() => modal.showModal())

    modal.addEventListener('click', (e) => {
      if (e.detail === 0) return // ignore focus events

      const dialogDimensions = modal.getBoundingClientRect()
      if (
        e.clientX < dialogDimensions.left ||
        e.clientX > dialogDimensions.right ||
        e.clientY < dialogDimensions.top ||
        e.clientY > dialogDimensions.bottom
      ) {
        modal.close()
      }
    })

    if (closeBtn) closeBtn.addEventListener('click', () => modal.close())
  } else {
    modal.setAttribute('hidden', '')
    setupOpeners(() => modal.removeAttribute('hidden'))

    modal.addEventListener('click', (e) => {
      const dialogDimensions = modal.getBoundingClientRect()
      if (
        e.clientX < dialogDimensions.left ||
        e.clientX > dialogDimensions.right ||
        e.clientY < dialogDimensions.top ||
        e.clientY > dialogDimensions.bottom
      ) {
        modal.setAttribute('hidden', '')
      }
    })

    if (closeBtn) closeBtn.addEventListener('click', () => modal.setAttribute('hidden', ''))
  }
}
